import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import toaster from 'components/commons/toast/Toast'
import { LOGOUT } from 'redux/action/type'
import { store } from 'redux/store/store'
import { ERROR_DETAIL_CODE, HTTP_STATUS_CODE } from 'utils/constants/statusCode'
import { authHelper } from 'utils/helpers'
import { logOut } from 'utils/helpers/auth'
import axiosConfig from 'utils/plugins/axios'

interface IAxiosInterceptors {
  children: JSX.Element
}

interface IError {
  code: string
  message: string
}

const AxiosInterceptors = ({ children }: IAxiosInterceptors) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isSync, setIsSync] = useState<boolean>(false)

  const handleLogout = () => {
    logOut()
    store.dispatch({ type: LOGOUT })
    navigate('/login')
  }

  const handleErrorPermissionDenied = (code: string = '') => {
    switch (code) {
      case ERROR_DETAIL_CODE.PERMISSION_DENIED:
        toaster.error(
          { message: t('error.permission_denied') },
          {
            id: ERROR_DETAIL_CODE.PERMISSION_DENIED,
          },
        )
        handleLogout()
        break
      case ERROR_DETAIL_CODE.USER_INACTIVE:
        toaster.error(
          { message: t('error.user_inactive') },
          {
            id: ERROR_DETAIL_CODE.USER_INACTIVE,
          },
        )
        handleLogout()
        break
      case ERROR_DETAIL_CODE.IP_BLOCKED:
        toaster.error(
          { message: t('error.ip_blocked') },
          {
            id: ERROR_DETAIL_CODE.IP_BLOCKED,
          },
        )
        if (window.location.pathname !== '/login') {
          handleLogout()
        }
        break
      case ERROR_DETAIL_CODE.NOT_PREMIUM:
        navigate('/dashboard/graph')
        break
    }
  }

  useEffect(() => {
    const reqInterceptor = axiosConfig.interceptors.request.use(
      async (config: AxiosRequestConfig | any) => {
        if (authHelper.isAuth()) {
          config.headers = {
            Authorization: authHelper.accessToken(),
            'X-Timezone-Offset': new Date().getTimezoneOffset(),
          }
        }

        return config
      },
      (error: AxiosError) => Promise.reject(error),
    )

    const resInterceptor = axiosConfig.interceptors.response.use(
      (response: AxiosResponse) => Promise.resolve(response),
      (error: AxiosError<IError>) => {
        const { response } = error
        const statusCode = response?.status
        const errorCodeDetail = response?.data.code
        const isNetworkError = error.code === HTTP_STATUS_CODE.NETWORK

        if (isNetworkError) {
          toaster.error(
            { message: t('error.network') },
            {
              id: HTTP_STATUS_CODE.NETWORK,
            },
          )
          return Promise.reject(response)
        }

        switch (statusCode) {
          case HTTP_STATUS_CODE.PERMISSION_DENIED:
            handleErrorPermissionDenied(errorCodeDetail)
            break
          case HTTP_STATUS_CODE.UNAUTHORIZED:
            toaster.error(
              { message: t('error.unauthorized') },
              {
                id: String(HTTP_STATUS_CODE.UNAUTHORIZED),
              },
            )
            handleLogout()
            break
          case HTTP_STATUS_CODE.SERVER_ERROR:
            toaster.error(
              { message: t('error.something_wrong') },
              {
                id: String(HTTP_STATUS_CODE.SERVER_ERROR),
              },
            )
            break
        }
        return Promise.reject(response)
      },
    )

    setIsSync(true)
    return () => {
      axiosConfig.interceptors.request.eject(reqInterceptor)
      axiosConfig.interceptors.response.eject(resInterceptor)
    }
  }, [])

  return isSync ? children : null
}

export default AxiosInterceptors
